import { CartAddEvent } from '@theme/events';

/*
 * LINK cart drawer add-ons (replace Upcart).
 *   <cart-rewards>  converts the tier thresholds when the customer shops in a non-shop currency.
 *                   In the shop currency the bar is fully rendered by Liquid (snippets/cart-rewards.liquid).
 *   <cart-upsell>   Upcart's upsell algorithm (read from Upcart's own code): Shopify
 *                   recommendations for every product in the cart, 8 each, merged in cart
 *                   order without duplicates; skips products already in the cart and sold-out
 *                   products/variants; first N shown. Adds to cart without a reload.
 */

/** @param {number} cents */
function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'USD';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(
      cents / 100
    );
  } catch {
    return (cents / 100).toFixed(2);
  }
}

class CartRewards extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;
  #applying = false;

  connectedCallback() {
    if (!this.hasAttribute('data-convert')) return;
    this.#apply();
    this.#observer = new MutationObserver(() => {
      if (!this.#applying) this.#apply();
    });
    this.#observer.observe(this, { attributes: true, childList: true, subtree: true, characterData: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #apply() {
    const rate = parseFloat(window.Shopify?.currency?.rate || '1') || 1;
    const shipBasis = Number(this.dataset.shipBasis || 0);
    const tier2Basis = Number(this.dataset.tier2Basis || 0);
    const tier1 = this.dataset.tier1 ? Math.round(Number(this.dataset.tier1) * rate) : null;
    const tier2 = this.dataset.tier2 ? Math.round(Number(this.dataset.tier2) * rate) : null;
    const tier2Applied = this.dataset.tier2Applied === 'true';

    const tier1Done = tier1 !== null && shipBasis >= tier1;
    const tier2Done = tier2 !== null && (tier2Applied || tier2Basis >= tier2);

    let key = 'after';
    let remaining = 0;
    if (tier1 !== null && !tier1Done) {
      key = 'tier1';
      remaining = tier1 - shipBasis;
    } else if (tier2 !== null && !tier2Done) {
      key = 'tier2';
      remaining = tier2 - tier2Basis;
    }

    const template = /** @type {HTMLTemplateElement | null} */ (this.querySelector(`template[data-message="${key}"]`));
    const html = (template?.innerHTML || '').replace(/\{\{\s*amount\s*\}\}/g, formatMoney(Math.max(0, remaining)));

    this.#applying = true;
    const message = this.querySelector('.lnk-rewards__message');
    if (message && message.innerHTML.trim() !== html.trim()) message.innerHTML = html;

    const fills = {
      tier1: tier1 === null ? 0 : Math.min(100, (shipBasis / tier1) * 100),
      tier2: 0,
    };
    if (tier2 !== null) {
      if (tier2Done) fills.tier2 = 100;
      else if (tier1 === null) fills.tier2 = Math.min(100, (tier2Basis / tier2) * 100);
      else if (tier1Done) fills.tier2 = Math.max(0, Math.min(100, ((tier2Basis - tier1) / (tier2 - tier1)) * 100));
    }
    for (const [name, fill] of Object.entries(fills)) {
      const segment = this.querySelector(`[data-segment="${name}"]`);
      if (!segment) continue;
      segment.querySelector('.lnk-rewards__bar')?.setAttribute('style', `--fill: ${fill.toFixed(2)}%;`);
      segment
        .querySelector('.lnk-rewards__tier')
        ?.classList.toggle('is-done', name === 'tier1' ? tier1Done : tier2Done);
    }
    this.classList.add('is-converted');
    queueMicrotask(() => (this.#applying = false));
  }
}

if (!customElements.get('cart-rewards')) customElements.define('cart-rewards', CartRewards);

/** @type {Map<string, Promise<any[]>>} */
const recommendationCache = new Map();

/**
 * @param {string} productId
 * @param {string} intent
 * @returns {Promise<any[]>}
 */
function getRecommendations(productId, intent) {
  const root = window.Shopify?.routes?.root || '/';
  const url = `${root}recommendations/products.json?product_id=${productId}&limit=8&intent=${intent}`;
  let request = recommendationCache.get(url);
  if (!request) {
    request = fetch(url)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((data) => data.products || [])
      .catch(() => {
        recommendationCache.delete(url);
        return [];
      });
    recommendationCache.set(url, request);
  }
  return request;
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] || c);
}

/**
 * Shopify money format ("${{amount}}", "€{{amount_with_comma_separator}}", …)
 * @param {number} cents
 * @param {string} format
 */
function shopifyMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const value = Number(cents) / 100;
  const group = (/** @type {number} */ n, /** @type {number} */ d, thousands = ',', decimal = '.') => {
    const [int, dec] = n.toFixed(d).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0);
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2);
    }
  });
}

/** @param {string | undefined | null} src @param {number} width */
function sizedImage(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? `https:${src}` : src;
  return url + (url.includes('?') ? '&' : '?') + `width=${width}`;
}

class CartUpsell extends HTMLElement {
  static observedAttributes = ['product-ids'];

  /** @type {string | null} */
  #renderedKey = null;

  /** @type {Map<number, any>} */
  #products = new Map();

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#load();
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-track]'));
  }

  async #load() {
    const key = this.getAttribute('product-ids') || '';
    if (key === this.#renderedKey) return;
    this.#renderedKey = key;

    const cartIds = key
      .split(',')
      .map((id) => Number(id))
      .filter(Boolean);
    const intent = this.dataset.intent || 'related';
    const limit = Number(this.dataset.limit || 5);

    // One request per distinct product, kept in cart order (Upcart requests per line)
    const sources = [...new Set(cartIds)];
    const lists = await Promise.all(sources.map((id) => getRecommendations(String(id), intent)));
    if (key !== this.#renderedKey) return; // cart changed meanwhile

    /** @type {any[]} */
    const merged = [];
    for (const product of lists.flat()) {
      if (!product || typeof product.id !== 'number') continue;
      if (merged.some((p) => p.id === product.id)) continue;
      merged.push(product);
    }

    const picked = [];
    for (const product of merged) {
      if (cartIds.includes(product.id)) continue; // already_in_cart
      if (!product.available) continue; // out_of_stock
      const variants = (product.variants || []).filter((/** @type {any} */ v) => v.available);
      if (!variants.length) continue; // no_available_variants
      picked.push({ ...product, variants });
      if (picked.length >= limit) break;
    }

    this.#products = new Map(picked.map((p) => [p.id, p]));
    this.#render(picked);
  }

  /** @param {any[]} products */
  #render(products) {
    const root = this.querySelector('[data-upsell-root]');
    if (!root) return;
    const format = this.dataset.moneyFormat || '';
    const button = escapeHtml(this.dataset.button || 'Add');

    if (!products.length) {
      root.innerHTML = '';
      return;
    }

    const slides = products
      .map((product) => {
        const variant = product.variants[0];
        const image = variant.featured_image?.src || product.featured_image;
        // Upcart shows the dropdown for every product except a lone "Default Title" variant
        const showSelect = !(product.variants.length === 1 && /^Default Title$/i.test(product.variants[0].title));
        const options = product.variants
          .map((/** @type {any} */ v) => `<option value="${v.id}">${escapeHtml(v.title)}</option>`)
          .join('');
        return `
          <div class="lnk-upsell__slide">
            <div class="lnk-upsell__item" data-upsell-item data-product-id="${product.id}">
              <a class="lnk-upsell__media" href="${escapeHtml(product.url)}" tabindex="-1">
                ${image ? `<img src="${escapeHtml(sizedImage(image, 180))}" alt="${escapeHtml(product.title)}" width="90" height="90" loading="lazy" data-upsell-image>` : ''}
              </a>
              <div class="lnk-upsell__info">
                <a class="lnk-upsell__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
                <p class="lnk-upsell__price" data-upsell-price>${this.#priceHtml(variant, format)}</p>
                <div class="lnk-upsell__actions">
                  ${showSelect ? `<select class="lnk-upsell__select" data-upsell-variant aria-label="${escapeHtml(product.title)}">${options}</select>` : ''}
                  <button type="button" class="lnk-upsell__add" data-upsell-add data-variant-id="${variant.id}">${button}</button>
                </div>
              </div>
            </div>
          </div>`;
      })
      .join('');

    const single = products.length < 2;
    root.innerHTML = `
      <div class="lnk-upsell__carousel">
        <button type="button" class="lnk-upsell__arrow" data-upsell-prev aria-label="Previous" ${single ? 'hidden' : ''}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>
        </button>
        <div class="lnk-upsell__window">
          <div class="lnk-upsell__track" data-upsell-track>${slides}</div>
        </div>
        <button type="button" class="lnk-upsell__arrow" data-upsell-next aria-label="Next" ${single ? 'hidden' : ''}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>
        </button>
      </div>`;
    this.#updateArrows();
    this.#track?.addEventListener('scroll', this.#updateArrows, { passive: true });
  }

  /** @param {any} variant @param {string} format */
  #priceHtml(variant, format) {
    const compare =
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="lnk-upsell__compare">${shopifyMoney(variant.compare_at_price, format)}</s>`
        : '';
    return `${compare}<span>${shopifyMoney(variant.price, format)}</span>`;
  }

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;
    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    const max = track.scrollWidth - track.clientWidth - 2;
    prev?.classList.toggle('is-disabled', track.scrollLeft <= 2);
    next?.classList.toggle('is-disabled', track.scrollLeft >= max);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!select.matches?.('[data-upsell-variant]')) return;
    const item = /** @type {HTMLElement | null} */ (select.closest('[data-upsell-item]'));
    const product = this.#products.get(Number(item?.dataset.productId));
    const variant = product?.variants.find((/** @type {any} */ v) => String(v.id) === select.value);
    if (!item || !variant) return;
    const price = item.querySelector('[data-upsell-price]');
    if (price) price.innerHTML = this.#priceHtml(variant, this.dataset.moneyFormat || '');
    const image = /** @type {HTMLImageElement | null} */ (item.querySelector('[data-upsell-image]'));
    if (image && variant.featured_image?.src) image.src = sizedImage(variant.featured_image.src, 180);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const arrow = target.closest('[data-upsell-prev], [data-upsell-next]');
    if (arrow) {
      const track = this.#track;
      if (track) {
        const step = track.clientWidth;
        const dir = arrow.hasAttribute('data-upsell-next') ? 1 : -1;
        const max = track.scrollWidth - track.clientWidth;
        // Loop around at the ends, like Upcart's carousel
        let next = track.scrollLeft + dir * step;
        if (next > max + 2) next = 0;
        if (next < -2) next = max;
        track.scrollTo({ left: next, behavior: 'smooth' });
      }
      return;
    }
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('[data-upsell-add]'));
    if (button) this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const item = button.closest('[data-upsell-item]');
    const select = /** @type {HTMLSelectElement | null} */ (item?.querySelector('[data-upsell-variant]'));
    const variantId = select ? select.value : button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    try {
      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      this.dispatchEvent(
        new CartAddEvent({}, 'cart-upsell', {
          source: 'cart-upsell',
          variantId: String(variantId),
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
